var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { Deferred } from "../../deferred";
import { decodeError, encodeError, isEncodedError } from "../../errors/encoder";
import { addTimeout } from "../../utils";
var LONG_TIMEOUT = 10000;
/**
 * MessageChannel handles the sending and receiving of messages via
 * `postMessage`.
 *
 * It defines the message-passing protocol between the Plugin Manager, the
 * Plugin Client, and credential selector pieces; it should be used by all of
 * them.
 *
 * There should be one of these on each end of a connection. The Plugin Manager
 * may have several: One for each entity it's communicating with.
 */
var MessageChannel = /** @class */ (function () {
    /**
     * Create a MessageChannel
     *
     * @param {Object} options Configuration for the channel. See below
     * descriptions for valid fields.
     *
     * @param {string} options.name What this window will be called. Messages
     * will be sent with this name as a prefix. For backward compatibility with
     * the pre-1.0.0 Plugin Client, the Plugin Manager must choose "host".
     *
     * @param {Object} options.other The Window that will receive sent messages,
     * and from which messages will be received.
     *
     * @param {string | null} options.otherOrigin The origin of the Window. This
     * can't be automatically computed from the Window itself, because accessing
     * a Window across domains is disallowed. If the Window will be sandboxed,
     * set this to null to disable origin checking.
     *
     * @param {Object} options.handlers An object containing message handlers.
     * Each key of the object should be a message name, with the corresponding
     * value being a function that will be called when a message of that name is
     * received.
     *
     * @param {string} options.instanceId [DEPRECATED] If `options.instanceId` is
     * set, each message sent by this MessageChannel will include the field
     * "instanceID", with its value equal to `options.instanceId`. This field was
     * historically used by the host to disambiguate responses from different
     * plugins, though the source window is now used for that.
     */
    function MessageChannel(params) {
        if (params === void 0) { params = {}; }
        var _a = params.name, name = _a === void 0 ? 'unknown' : _a, _b = params.other, other = _b === void 0 ? window : _b, _c = params.otherOrigin, otherOrigin = _c === void 0 ? '' : _c, _d = params.handlers, handlers = _d === void 0 ? {} : _d, instanceId = params.instanceId;
        this.name = name;
        this.other = other;
        this.otherOrigin = otherOrigin;
        this.handlers = handlers;
        this.window = window;
        this.instanceId = instanceId;
        this.pendingMessages = {};
        this.nextMessageId = 1; // unique per message
        var asyncBoundHandleMessage = this.handleMessage.bind(this);
        this.handleMessageFunc = function (messageEvent) {
            void asyncBoundHandleMessage(messageEvent);
        };
        this.window.addEventListener('message', this.handleMessageFunc);
    }
    /**
     * Tear down the MessageChannel, removing the event listener on the window.
     */
    MessageChannel.prototype.destroy = function () {
        this.window.removeEventListener('message', this.handleMessageFunc);
    };
    /**
     * Set the instanceId for this channel.
     */
    MessageChannel.prototype.setInstanceId = function (instanceId) {
        this.instanceId = instanceId;
    };
    /**
     * postMessage sends a message through the channel. It returns a Promise that
     * will be resolved with the other's response, or rejected if the message
     * isn't responded to within the timeout.
     */
    MessageChannel.prototype.postMessage = function (name_1, data_1) {
        return __awaiter(this, arguments, void 0, function (name, data, timeout) {
            var id, messageName, message, timeoutPromise, response;
            if (timeout === void 0) { timeout = LONG_TIMEOUT; }
            return __generator(this, function (_a) {
                id = "".concat(this.name, ".").concat(this._getMessageId());
                messageName = "".concat(this.name, ".").concat(name);
                message = JSON.stringify({
                    id: id,
                    instanceID: this.instanceId,
                    name: messageName,
                    data: data,
                });
                timeoutPromise = new Promise(function (resolve, reject) {
                    addTimeout(timeout, reject, { name: name, data: data });
                });
                response = new Deferred();
                this.pendingMessages[id] = response;
                this._postMessage(message);
                return [2 /*return*/, Promise.race([response.promise, timeoutPromise])];
            });
        });
    };
    /*
      Registers a function to be called when the given event name is posted to the iframe.
    */
    MessageChannel.prototype.registerHandler = function (name, handlerFunc) {
        if (typeof handlerFunc !== 'function') {
            return false;
        }
        this.handlers[name] = handlerFunc;
        return true;
    };
    /*
      Removes the handler associated with the event given.
    */
    MessageChannel.prototype.removeHandler = function (name) {
        if (!this.handlers[name]) {
            return false;
        }
        delete this.handlers[name];
        return true;
    };
    /**
     * Handle an incoming message. A bound version of this function is set as an
     * event listener on the Window.
     *
     * @param {MessageEvent} message - The message to be handled.
     *
     * @returns {Promise} that resolves to undefined after the message is
     * handled, including any necessary responses sent to the plugin (which may
     * await Promises as needed to evaluate the response).
     */
    MessageChannel.prototype.handleMessage = function (messageEvent) {
        return __awaiter(this, void 0, void 0, function () {
            var maybeMessage, message, resultPromise, _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        maybeMessage = this.parseMessage(messageEvent);
                        if (!maybeMessage) {
                            return [2 /*return*/];
                        }
                        message = maybeMessage;
                        if (message.id && message.id in this.pendingMessages) {
                            // This message is a reply. Resolve the relevant Promise.
                            this._resolvePromise(message.id, message);
                            return [2 /*return*/];
                        }
                        if (message.id && message.id.startsWith(this.name)) {
                            // Error: The id indicates this is originally from our side of the
                            // channel, but it wasn't caught by the reply filter above.
                            return [2 /*return*/];
                        }
                        resultPromise = this.dispatchMessage(message.name, message.data);
                        _a = this._sendResponse;
                        _b = [message];
                        return [4 /*yield*/, this.generateResponseFields(resultPromise)];
                    case 1:
                        _a.apply(this, _b.concat([_c.sent()]));
                        return [2 /*return*/];
                }
            });
        });
    };
    MessageChannel.prototype.generateResponseFields = function (p) {
        return __awaiter(this, void 0, void 0, function () {
            var e_1;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        _a = {};
                        return [4 /*yield*/, p];
                    case 1: return [2 /*return*/, (_a.result = _b.sent(), _a.error = '', _a)];
                    case 2:
                        e_1 = _b.sent();
                        if (e_1 instanceof Error) {
                            return [2 /*return*/, {
                                    error: e_1.message,
                                    richError: encodeError(e_1),
                                }];
                        }
                        else if (typeof e_1 === 'string') {
                            return [2 /*return*/, { error: e_1 }];
                        }
                        else if (e_1 && typeof e_1 === 'object') {
                            return [2 /*return*/, { error: e_1 }];
                        }
                        else {
                            throw e_1;
                        }
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * @returns {Object|undefined} The parsed message body, or undefined if the
     * message isn't valid.
     */
    MessageChannel.prototype.parseMessage = function (message) {
        if (message.source !== this.other) {
            return; // wrong source
        }
        if (!this._isValidOrigin(message.origin)) {
            return; // wrong origin
        }
        var rawMessageData = message.data;
        if (!rawMessageData) {
            return; // no data in message
        }
        if (typeof rawMessageData !== 'string') {
            return; // unexpected data format in message
        }
        var messageData;
        try {
            messageData = JSON.parse(rawMessageData);
        }
        catch (err) {
            return; // could not parse data
        }
        if (typeof messageData !== 'object') {
            return; // unexpected data format in parsed message
        }
        if (!messageData) {
            return; // unexpected data format in parsed message
        }
        if ('name' in messageData === false) {
            return; // no message name found
        }
        return messageData;
    };
    MessageChannel.prototype._isValidOrigin = function (origin) {
        // Plugins can (and should) be sandboxed, which means they have no origin
        // (or, more accurately, "a special origin that always fails the
        // same-origin policy" -- Chrome uses the string "null"). To allow
        // cross-window communication anyway, don't check the origin. (Callers
        // should always being verifying the source of the message as well.)
        if (this.otherOrigin === null)
            return true;
        return origin === this.otherOrigin;
    };
    MessageChannel.prototype._sendResponse = function (originalMessage, result) {
        var name = originalMessage.name.includes('.')
            ? originalMessage.name.split('.')[1]
            : originalMessage.name;
        var messageObject = Object.assign({}, result, {
            name: "".concat(this.name, ".").concat(name),
            id: originalMessage.id || "".concat(this.name, ".").concat(this._getMessageId()),
            instanceID: this.instanceId,
        });
        var message = JSON.stringify(messageObject);
        this._postMessage(message);
    };
    /**
     * Find the right handler for a message and call it, returning the handler's
     * return value.
     *
     * This is a very simple function but is on its own because the plugin client
     * calls it directly for init messages (pretending that the host has sent an
     * init message, when actually the client starts sends the init event).
     *
     * @param {string} name - The message name, the key on which dispatch is
     * based.
     *
     * @param {Object} data - The data to be passed to the correct handler.
     *
     * @returns The handler's return value
     */
    MessageChannel.prototype.dispatchMessage = function (name, data) {
        return __awaiter(this, void 0, void 0, function () {
            var handler;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        handler = this.getHandler(name);
                        if (!handler) {
                            throw new Error("No handlers found for message '".concat(name, "'."));
                        }
                        return [4 /*yield*/, handler(data)];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Find the right handler for a message, using its name.
     *
     * @param {string} name - The message name, the key on which dispatch is
     * based.
     *
     * @returns {function | undefined} The correct handler function, which should be
     * invoked with the message data. May return undefined, if there is no matching
     * handler.
     */
    MessageChannel.prototype.getHandler = function (name) {
        // Some components (e.g. credential selector) route messages with full
        // names. The manager and client, though, use the non-prefixed names. Try
        // both, starting with the full name.
        if (name in this.handlers) {
            return this.handlers[name];
        }
        // No matches with the full name. Try the short name, cutting off the
        // prefix.
        var prefixEnd = name.indexOf('.');
        if (prefixEnd !== -1) {
            var shortName = name.slice(prefixEnd + 1);
            if (shortName in this.handlers) {
                return this.handlers[shortName];
            }
        }
        return;
    };
    MessageChannel.prototype._resolvePromise = function (id, data) {
        if (id in this.pendingMessages === false) {
            throw new Error("Tried to resolve unknown pending message ".concat(id));
        }
        var messageController = this.pendingMessages[id];
        delete this.pendingMessages[id];
        if (isEncodedError(data.richError)) {
            messageController.reject(decodeError(data.richError));
        }
        if (data.error) {
            if (typeof data.error === 'string') {
                messageController.reject(new Error(data.error));
            }
            else {
                messageController.reject(data.error);
            }
            return;
        }
        messageController.resolve(data.result);
    };
    MessageChannel.prototype._getMessageId = function () {
        return this.nextMessageId++;
    };
    MessageChannel.prototype._postMessage = function (message) {
        this.other.postMessage(message, this._getTargetOrigin());
    };
    MessageChannel.prototype._getTargetOrigin = function () {
        // If we're communicating with a sandboxed plugin (i.e. the otherOrigin is
        // null), we must send the message with a target origin of '*', indicating
        // no preference.
        if (this.otherOrigin === null) {
            return '*';
        }
        return this.otherOrigin;
    };
    return MessageChannel;
}());
export default MessageChannel;
//# sourceMappingURL=messageChannel.js.map